import { memo, useCallback, useEffect, useState } from "react";
import * as React from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  PlusCircle,
  XCircleFill,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";

type Props = {
  chainId: string | number | null;
  value?: string[] | null;
  onChange: (value?: string[] | null) => void;
  maxPrices?: number;
  error?: string | null;
  forceDisable?: boolean;
};

const SAFE_FLOAT_ROUNDING_PRECISION = 6;

const DutchAuctionsCurrencyInput: React.FC<Props> = ({
  chainId,
  maxPrices = 5,
  error,
  onChange,
  value,
  forceDisable,
}) => {
  const [priceInput, setPriceInput] = useState<number | undefined>();
  const [tooltipLabel, setTooltipLabel] = useState<string>("");
  const [placeholder, setPlaceholder] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (!chainId) {
      setDisabled(true);
      setPlaceholder("Select a blockchain before entering price");
      return;
    }
    if (!value?.length || value?.length === 0) {
      setPlaceholder("Add a price");
      return;
    }
    if (value?.length >= maxPrices) {
      setDisabled(true);
      setTooltipLabel(`Up to ${maxPrices} prices are allowed`);
      setPlaceholder("Can't add more prices");
      return;
    }

    setTooltipLabel("");
    setDisabled(false);
    setPlaceholder("Add another price");
  }, [value, maxPrices, chainId, priceInput]);

  const handleAddPrice = useCallback(() => {
    if (priceInput === null || priceInput === undefined) return;
    onChange([...(value || []), priceInput.toString()].sort((a, b) => +b - +a));
    setPriceInput(undefined);
  }, [onChange, value, priceInput]);

  const handleRemovePrice = useCallback(
    (index: number) => {
      if (!value?.length) return;
      onChange(value.filter((price, i) => index !== i));
    },
    [onChange, value]
  );

  return (
    <Box>
      <Text component="label" size="sm">
        Prices
      </Text>
      <Text size="sm">
        Specify every price point your auction should descend through. The price
        will drop at your chosen time interval
      </Text>
      <BoxContainer mt={8} px="md" pt="xs" pb={14} error={!!error}>
        <Tooltip label={tooltipLabel} disabled={!tooltipLabel}>
          <NumberInput
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (!disabled) handleAddPrice();
              }
            }}
            onChange={(value) => setPriceInput(value)}
            precision={SAFE_FLOAT_ROUNDING_PRECISION}
            value={priceInput}
            styles={{
              input: {
                padding: 0,
                margin: 0,
                border: "none",
                height: 28,
              },
            }}
            h={28}
            mb={4}
            placeholder={placeholder}
            rightSectionWidth={99}
            rightSection={
              <Button
                color={SECONDARY_COLOR}
                leftIcon={<PlusCircle />}
                disabled={forceDisable || disabled}
                size="xs"
                onClick={handleAddPrice}
              >
                Add price
              </Button>
            }
            min={0}
            step={0}
            removeTrailingZeros={true}
            disabled={forceDisable || disabled}
          />
        </Tooltip>
        {value?.map((price, index) => (
          <Stack spacing="xs" key={index}>
            <Divider mt="xs" />
            <Group position="apart">
              <Text size="sm">
                {price} {getCurrencySymbol(chainId || "")}
              </Text>
              <ActionIcon onClick={() => handleRemovePrice(index)}>
                <XCircleFill />
              </ActionIcon>
            </Group>
          </Stack>
        ))}
      </BoxContainer>
      <Text color={STATUS_COLOR.ERROR} size="xs" mt="xs">
        {error}
      </Text>
    </Box>
  );
};

export default memo(DutchAuctionsCurrencyInput);
